import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calculator, Upload, Plus, Trash2, FileText, Save,
  CheckCircle, AlertCircle, Info, Package, Layers, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import budgetSheetService from '../services/budgetSheetService';
import cortesPdfService from '../services/cortesPdfService';

const BudgetSheetPricing = ({ survey, budgetSheet, onCalculated }) => {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    complexity_level: budgetSheet?.complexity_level || 3,
    material_type: budgetSheet?.material_type || 'MDF_STANDARD',
    hardware_type: budgetSheet?.hardware_type || 'BASICO',
    door_finish: budgetSheet?.door_finish || 'FOSCO',
    has_glass: budgetSheet?.has_glass || false,
    base_area_m2: budgetSheet?.calculated_pricing_data?.base_area_m2 || '',
    base_price: budgetSheet?.calculated_pricing_data?.base_price || '',
    margin_percent: 25
  });
  
  const [consumables, setConsumables] = useState([]);
  const [newConsumable, setNewConsumable] = useState({ name: '', quantity: 1, unit_price: 0 });
  const [cortesPdf, setCortesPdf] = useState(null);
  const [calculatedResult, setCalculatedResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedPdfData, setParsedPdfData] = useState(null);
  const [showParsedPdf, setShowParsedPdf] = useState(false);
  const [pdfParsing, setPdfParsing] = useState(false);
  const fileRef = useRef(null);

  const { data: pricingConfig, isLoading: configLoading } = useQuery({
    queryKey: ['budget-sheet-pricing-config'],
    queryFn: () => budgetSheetService.getPricingConfig(),
    enabled: !!budgetSheet?.id
  });

  const { data: existingConsumables } = useQuery({
    queryKey: ['budget-sheet-consumables', budgetSheet?.id],
    queryFn: () => budgetSheetService.getConsumables(survey.id, budgetSheet.id),
    enabled: !!budgetSheet?.id
  });

  const { data: existingPdf } = useQuery({
    queryKey: ['budget-sheet-cortes-pdf', budgetSheet?.id],
    queryFn: () => budgetSheetService.getCortesPdf(survey.id, budgetSheet.id),
    enabled: !!budgetSheet?.id
  });

  useEffect(() => {
    if (existingConsumables?.data) {
      setConsumables(existingConsumables.data);
    }
  }, [existingConsumables]);

  useEffect(() => {
    if (existingPdf?.data) {
      setCortesPdf(existingPdf.data);
    }
  }, [existingPdf]);

  const calculateMutation = useMutation({
    mutationFn: (data) => budgetSheetService.calculatePricing(survey.id, budgetSheet.id, data),
    onSuccess: (response) => {
      if (response.success) {
        setCalculatedResult(response.data);
        toast.success('Preço calculado com sucesso!');
        if (onCalculated) {
          onCalculated(response.data);
        }
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao calcular preço');
    }
  });

  const addConsumableMutation = useMutation({
    mutationFn: (data) => budgetSheetService.addConsumable(survey.id, budgetSheet.id, data),
    onSuccess: (response) => {
      if (response.success) {
        setConsumables([...consumables, response.data]);
        setNewConsumable({ name: '', quantity: 1, unit_price: 0 });
        toast.success('Consumível adicionado!');
      }
    },
    onError: () => {
      toast.error('Erro ao adicionar consumível');
    }
  });

  const removeConsumableMutation = useMutation({
    mutationFn: (consumableId) => budgetSheetService.removeConsumable(survey.id, budgetSheet.id, consumableId),
    onSuccess: (_, consumableId) => {
      setConsumables(consumables.filter(c => c.id !== consumableId));
      toast.success('Consumível removido!');
    },
    onError: () => {
      toast.error('Erro ao remover consumível');
    }
  });

  const uploadPdfMutation = useMutation({
    mutationFn: (file) => budgetSheetService.uploadCortesPdf(survey.id, budgetSheet.id, file),
    onSuccess: async (response) => {
      if (response.success) {
        setCortesPdf(response.data);
        toast.success('PDF enviado com sucesso!');
        if (fileRef.current) {
          setPdfParsing(true);
          try {
            const parseResponse = await cortesPdfService.processPdf(fileRef.current);
            const pdfId = parseResponse.data?.id || parseResponse?.data?.pdf_id;
            if (pdfId) {
              const detalhesResponse = await cortesPdfService.getPdfDetalhes(pdfId);
              setParsedPdfData(detalhesResponse.data);
              setShowParsedPdf(true);
              toast.success('PDF processado com sucesso!');
            }
          } catch (error) {
            console.error('Erro ao processar PDF:', error);
            toast.error('PDF enviado, mas erro ao processar');
          } finally {
            setPdfParsing(false);
          }
        }
      }
    },
    onError: () => {
      toast.error('Erro ao fazer upload do PDF');
    }
  });

  const parsePdfMutation = useMutation({
    mutationFn: (file) => cortesPdfService.processPdf(file),
    onSuccess: async (response) => {
      const pdfId = response.data?.id || response?.data?.pdf_id;
      if (pdfId) {
        try {
          const detalhesResponse = await cortesPdfService.getPdfDetalhes(pdfId);
          setParsedPdfData(detalhesResponse.data);
          setShowParsedPdf(true);
          toast.success('PDF processado com sucesso!');
        } catch (error) {
          toast.error('Erro ao obter detalhes do PDF');
        }
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao processar PDF');
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = () => {
    if (!formData.base_area_m2 || !formData.base_price) {
      toast.error('Informe a área base e o preço base');
      return;
    }
    
    const consumablesData = consumables.map(c => ({
      name: c.name,
      quantity: c.quantity,
      unit_price: c.unit_price,
      total_price: c.total_price
    }));

    calculateMutation.mutate({
      ...formData,
      consumables: consumablesData
    });
  };

  const handleAddConsumable = () => {
    if (!newConsumable.name || !newConsumable.unit_price) {
      toast.error('Informe nome e preço do consumível');
      return;
    }
    addConsumableMutation.mutate(newConsumable);
  };

  const handleRemoveConsumable = (id) => {
    removeConsumableMutation.mutate(id);
  };

  const handlePdfUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      fileRef.current = file;
      setUploading(true);
      uploadPdfMutation.mutate(file);
    } else {
      toast.error('Apenas arquivos PDF são permitidos');
    }
  };

  const complexityOptions = pricingConfig?.data?.complexity || [];
  const materialOptions = pricingConfig?.data?.materials || [];
  const hardwareOptions = pricingConfig?.data?.hardware || [];
  const doorFinishOptions = pricingConfig?.data?.doorFinishes || [];

  const getComplexityLabel = (level) => {
    const labels = {
      1: 'Muito Simples',
      2: 'Simples',
      3: 'Normal',
      4: 'Complexo',
      5: 'Muito Complexo'
    };
    return labels[level] || `Nível ${level}`;
  };

  const CHAPA_AREA_M2 = 2.75 * 1.85;

  const dadosExtras = parsedPdfData?.dados_extras || {};
  const planos = dadosExtras?.planos || [];
  const qtdChapas = dadosExtras?.quantidade_chapas || 0;
  const totalChapasPlanos = planos.reduce((total, plano) => total + (plano.qtd || 0), 0);
  const aproveitamentoMedio = totalChapasPlanos > 0
    ? planos.reduce((total, plano) => total + ((plano.qtd || 0) * (plano.aproveitamento || 0)), 0) / totalChapasPlanos
    : 0;
  const areaUtil = planos.reduce((total, plano) => {
    const qtd = plano.qtd || 0;
    const aproveitamento = plano.aproveitamento || 0;
    return total + (qtd * aproveitamento / 100 * CHAPA_AREA_M2);
  }, 0);
  const areaTotalChapas = qtdChapas * CHAPA_AREA_M2;

  if (!budgetSheet) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">Selecione uma ficha de orçamento para precificar</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Precificação da Ficha</h3>
          <span className="text-sm text-gray-500 ml-auto">
            {budgetSheet.ambiente_tipo || budgetSheet.model_name || 'Sem ambiente'}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Configure os parâmetros de precificação</p>
            <p className="mt-1">O sistema calculará automaticamente o preço baseado na complexidade, material e demais fatores.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Layers className="w-4 h-4 inline mr-1" />
              Nível de Complexidade
            </label>
            <select
              value={formData.complexity_level}
              onChange={(e) => handleInputChange('complexity_level', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {complexityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.multiplier}x)
                </option>
              ))}
              {!complexityOptions.length && [1,2,3,4,5].map(level => (
                <option key={level} value={level}>{getComplexityLabel(level)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Quanto mais complexo, maior o multiplicador de preço
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Package className="w-4 h-4 inline mr-1" />
              Tipo de Material
            </label>
            <select
              value={formData.material_type}
              onChange={(e) => handleInputChange('material_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {materialOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.weight > 0 ? `+${opt.weight}%` : '0%'})
                </option>
              ))}
              {!materialOptions.length && [
                { value: 'MDF_STANDARD', label: 'MDF Standard' },
                { value: 'MDF_PREMIUM', label: 'MDF Premium (+10%)' },
                { value: 'MDF_LACADO', label: 'MDF Lacado (+15%)' },
                { value: 'MDP', label: 'MDP (-5%)' },
                { value: 'OSB', label: 'OSB (-10%)' }
              ].map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ferragens
            </label>
            <select
              value={formData.hardware_type}
              onChange={(e) => handleInputChange('hardware_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {hardwareOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.weight > 0 ? `+${opt.weight}%` : '0%'})
                </option>
              ))}
              {!hardwareOptions.length && [
                { value: 'BASICO', label: 'Básico' },
                { value: 'PREMIUM', label: 'Premium (+8%)' },
                { value: 'BLUM', label: 'Blum (+15%)' }
              ].map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Acabamento das Portas
            </label>
            <select
              value={formData.door_finish}
              onChange={(e) => handleInputChange('door_finish', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {doorFinishOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.weight > 0 ? `+${opt.weight}%` : '0%'})
                </option>
              ))}
              {!doorFinishOptions.length && [
                { value: 'FOSCO', label: 'Fosco' },
                { value: 'BRILHANTE', label: 'Brilhante (+5%)' },
                { value: 'TEXTURIZADO', label: 'Texturizado (+3%)' }
              ].map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_glass}
                onChange={(e) => handleInputChange('has_glass', e.target.checked)}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">Possui Vidro</span>
              <span className="text-xs text-gray-500">(+5% taxa adicional)</span>
            </label>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Dados Base para Cálculo
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Área Base (m²)</label>
              <input
                type="number"
                step="0.01"
                value={formData.base_area_m2}
                onChange={(e) => handleInputChange('base_area_m2', e.target.value)}
                placeholder="Ex: 25.50"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Preço Base (R$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.base_price}
                onChange={(e) => handleInputChange('base_price', e.target.value)}
                placeholder="Ex: 150.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-800 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Plano de Corte (PDF)
            </h4>
            {parsedPdfData && (
              <button
                onClick={() => setShowParsedPdf(!showParsedPdf)}
                className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800"
              >
                <Eye className="w-4 h-4" />
                {showParsedPdf ? 'Ocultar' : 'Ver'} Peças
              </button>
            )}
          </div>

          {cortesPdf ? (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{cortesPdf.original_name}</p>
                    <p className="text-xs text-green-600">
                      {(cortesPdf.file_size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setCortesPdf(null);
                    setParsedPdfData(null);
                    setShowParsedPdf(false);
                  }}
                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {pdfParsing && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm text-purple-700">Processando PDF...</p>
                </div>
              )}

              {showParsedPdf && parsedPdfData && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h5 className="font-semibold text-purple-800 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Dados Extraídos do PDF
                    </h5>
                  </div>

                  {(dadosExtras.codigo_pedido || dadosExtras.descricao_pedido) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {dadosExtras.codigo_pedido && (
                          <div>
                            <span className="text-xs text-gray-600">Código:</span>
                            <p className="font-medium text-gray-800">{dadosExtras.codigo_pedido}</p>
                          </div>
                        )}
                        {dadosExtras.descricao_pedido && (
                          <div>
                            <span className="text-xs text-gray-600">Descrição:</span>
                            <p className="font-medium text-gray-800">{dadosExtras.descricao_pedido}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="grid grid-cols-4 gap-2 text-center text-sm">
                      <div>
                        <span className="text-xs text-gray-500">Material</span>
                        <p className="font-bold text-gray-800">{dadosExtras.material || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Espessura</span>
                        <p className="font-bold text-gray-800">{dadosExtras.espessura_mm ? dadosExtras.espessura_mm + 'mm' : '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Cor</span>
                        <p className="font-bold text-gray-800">{dadosExtras.cor || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Chapas</span>
                        <p className="font-bold text-blue-600">{qtdChapas}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <span className="text-xs text-gray-600">Qtd. Chapas</span>
                        <p className="text-xl font-bold text-gray-800">{qtdChapas}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Área Total</span>
                        <p className="text-xl font-bold text-blue-600">{areaTotalChapas.toFixed(2)} m²</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Área Útil</span>
                        <p className="text-xl font-bold text-green-600">{areaUtil.toFixed(2)} m²</p>
                      </div>
                    </div>
                  </div>

                  {planos.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h6 className="text-sm font-bold text-gray-800 mb-2">Planos de Corte</h6>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg border border-green-300 text-xs">
                          <thead className="bg-green-100">
                            <tr>
                              <th className="px-2 py-1 text-left font-bold text-gray-700">Plano</th>
                              <th className="px-2 py-1 text-center font-bold text-gray-700">Qtd</th>
                              <th className="px-2 py-1 text-center font-bold text-gray-700">Peças</th>
                              <th className="px-2 py-1 text-center font-bold text-gray-700">Sobras</th>
                              <th className="px-2 py-1 text-center font-bold text-gray-700">Cortes</th>
                              <th className="px-2 py-1 text-center font-bold text-gray-700">Aprov.</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-green-200">
                            {planos.map((plano, idx) => (
                              <tr key={idx} className="hover:bg-green-50">
                                <td className="px-2 py-1 font-bold text-gray-800">
                                  {plano.numero}/{plano.total || '?'}
                                </td>
                                <td className="px-2 py-1 text-center text-gray-700">
                                  {plano.qtd !== undefined ? plano.qtd : '-'}
                                </td>
                                <td className="px-2 py-1 text-center font-semibold text-gray-700">
                                  {plano.pecas !== undefined ? plano.pecas : '-'}
                                </td>
                                <td className="px-2 py-1 text-center text-gray-700">
                                  {plano.sobras !== undefined ? plano.sobras : '-'}
                                </td>
                                <td className="px-2 py-1 text-center text-gray-700">
                                  {plano.cortes !== undefined ? plano.cortes : '-'}
                                </td>
                                <td className="px-2 py-1 text-center">
                                  {plano.aproveitamento !== undefined ? (
                                    <span className="font-bold text-green-600">{plano.aproveitamento.toFixed(2)}%</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-sm">
                    <div>
                      <span className="text-gray-600">Qtd. Peças:</span>
                      <span className="font-bold text-gray-800 ml-1">{dadosExtras.quantidade_pecas || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Aproveitamento Médio:</span>
                      <span className="font-bold text-green-600 ml-1">{aproveitamentoMedio.toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">Arraste ou clique para enviar</p>
              <label className="cursor-pointer inline-block px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? 'Enviando...' : 'Escolher PDF'}
              </label>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Insumos e Consumíveis
          </h4>
          
          {consumables.length > 0 && (
            <div className="space-y-2 mb-3">
              {consumables.map(c => (
                <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.name}</p>
                    <p className="text-xs text-gray-500">
                      {c.quantity} x R$ {parseFloat(c.unit_price).toFixed(2)} = R$ {parseFloat(c.total_price).toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveConsumable(c.id)}
                    className="p-1 text-red-600 hover:bg-red-100 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            <input
              type="text"
              value={newConsumable.name}
              onChange={(e) => setNewConsumable(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nome"
              className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
            <input
              type="number"
              value={newConsumable.quantity}
              onChange={(e) => setNewConsumable(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
              placeholder="Qtd"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
            <input
              type="number"
              step="0.01"
              value={newConsumable.unit_price}
              onChange={(e) => setNewConsumable(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
              placeholder="R$ Unit"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
          </div>
          <button
            onClick={handleAddConsumable}
            className="mt-2 w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Adicionar Consumível
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={handleCalculate}
            disabled={calculateMutation.isPending}
            className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
          >
            {calculateMutation.isPending ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5" />
                Calcular Preço
              </>
            )}
          </button>
        </div>

        {calculatedResult && (
          <div className="border-t border-gray-200 pt-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-800">Resultado do Cálculo</h4>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal (base):</span>
                  <span className="font-medium">R$ {calculatedResult.subtotal?.toFixed(2)}</span>
                </div>
                
                {calculatedResult.pricing_data && (
                  <>
                    <div className="flex justify-between text-xs text-gray-500 pl-4">
                      <span>Complexidade ({calculatedResult.pricing_data.complexity_multiplier}x):</span>
                      <span>R$ {calculatedResult.pricing_data.with_complexity?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 pl-4">
                      <span>Material (+{calculatedResult.pricing_data.material_weight}%):</span>
                      <span>R$ {calculatedResult.pricing_data.with_material?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 pl-4">
                      <span>Ferragens (+{calculatedResult.pricing_data.hardware_weight}%):</span>
                      <span>R$ {calculatedResult.pricing_data.with_hardware?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 pl-4">
                      <span>Acabamento (+{calculatedResult.pricing_data.door_finish_weight}%):</span>
                      <span>R$ {calculatedResult.pricing_data.with_door_finish?.toFixed(2)}</span>
                    </div>
                    {calculatedResult.pricing_data.consumables_total > 0 && (
                      <div className="flex justify-between text-xs text-gray-500 pl-4">
                        <span>Insumos:</span>
                        <span>R$ {calculatedResult.pricing_data.consumables_total?.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedResult.pricing_data.options_weight_percentage > 0 && (
                      <div className="flex justify-between text-xs text-purple-700 font-semibold pl-4">
                        <span>Pesos Ficha Técnica (+{calculatedResult.pricing_data.options_weight_percentage}%):</span>
                        <span>+{calculatedResult.pricing_data.options_weight_percentage}%</span>
                      </div>
                    )}
                    {calculatedResult.pricing_data.options_fixed_cost > 0 && (
                      <div className="flex justify-between text-xs text-purple-700 font-semibold pl-4">
                        <span>Adicionais Fixos (Ficha Técnica):</span>
                        <span>+R$ {calculatedResult.pricing_data.options_fixed_cost?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-gray-500 pl-4">
                      <span>Margem ({calculatedResult.pricing_data.margin_percent}%):</span>
                      <span>R$ {calculatedResult.pricing_data.with_margin?.toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                <div className="border-t border-green-200 pt-2 flex justify-between items-center">
                  <span className="font-semibold text-green-800">PREÇO FINAL:</span>
                  <span className="text-xl font-bold text-green-700">
                    R$ {calculatedResult.calculated_total?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetSheetPricing;